import React from 'react';
import { Check, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PUBLIC_TIERS, Plan, PlanId, formatBytes, useUserPlan } from '../services/plan';
import { useAuth } from '../contexts/AuthContext';

// PricingTable — comparison-table variant of the plan cards. Rendered
// on the landing page in place of the old three-card PlansRow.
//
// Design rationale (per PRD § UX design):
//   Rows: what you're comparing. Price is row 1 (users still want to
//   see it), features fill the middle bulk, priority is last.
//   Columns: First Light · Full Stack · Tall Stack. The current column
//   gets a highlight when the viewer is authed.
//
// Not a purchase surface — no per-tier commit CTAs from anon (nobody
// buys $49/mo before touching the product). One footer row spans all
// three columns with a single CTA: "Sign in to get started" (anon) /
// "Manage your plan" (authed).

interface Row {
  label: string;
  render: (t: Plan) => React.ReactNode;
}

// Seestar S30 Pro FITS ≈ 16 MB per frame (8 MP × 2 bytes/pixel, uncompressed).
// Used to translate abstract storage caps into "how many nights fit" for
// pricing-table shoppers.
const S30P_FRAME_BYTES = 16 * 1024 * 1024;

const formatFrameCount = (n: number): string => {
  if (n >= 10000) return `${Math.round(n / 1000).toLocaleString()}K`;
  if (n >= 1000) return `${Math.round(n / 100) * 100 / 1000}K`;
  return Math.round(n).toLocaleString();
};

const yesNo = (v: boolean) =>
  v ? (
    <Check className="h-4 w-4 text-nebula-300 mx-auto" aria-label="included" />
  ) : (
    <Minus className="h-4 w-4 text-white/25 mx-auto" aria-label="not included" />
  );

const rows: Row[] = [
  {
    label: 'Monthly price',
    render: (t) =>
      t.monthlyUSD === 0 ? (
        <span className="font-semibold text-white/80">Free</span>
      ) : (
        <span className="font-display font-bold text-white">${t.monthlyUSD}/mo</span>
      ),
  },
  {
    label: 'Included credits / month',
    render: (t) => <span>{t.monthlyFrames.toLocaleString()}</span>,
  },
  {
    label: 'Storage',
    render: (t) => (
      <div className="leading-tight">
        <div>{formatBytes(t.storageBytes)}</div>
        <div className="text-xs text-white/45 mt-0.5">
          ≈ {formatFrameCount(t.storageBytes / S30P_FRAME_BYTES)} S30P frames
        </div>
      </div>
    ),
  },
  {
    label: 'Core features (Panalyzer, Filter, Compare, Sky map)',
    render: () => yesNo(true),
  },
  {
    label: 'Each frame analysed and plate-solved',
    render: () => yesNo(true),
  },
  {
    label: 'Split-stacking (fire off multiple stacks in parallel)',
    render: (t) => yesNo(t.id !== 'first-light'),
  },
  {
    label: 'Google Drive import',
    render: (t) => yesNo(t.id !== 'first-light'),
  },
  {
    label: 'Top-up packs',
    render: () => yesNo(true),
  },
];

const PricingTable: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { plan } = useUserPlan();
  const currentId: PlanId | null = isAuthenticated ? plan?.id ?? null : null;

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {/* Header row — tier names + subtitle. */}
        <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,minmax(0,1fr))] border-b border-white/[0.06]">
          <div className="p-4 text-xs uppercase tracking-widest text-white/40 font-semibold">
            &nbsp;
          </div>
          {PUBLIC_TIERS.map((t) => {
            const highlight = t.id === currentId;
            return (
              <div
                key={t.id}
                className={`p-4 border-l border-white/[0.06] text-center ${
                  highlight ? 'bg-nebula-500/[0.08]' : ''
                }`}
              >
                <div className="text-xs uppercase tracking-widest text-white/40 font-semibold">
                  {t.name}
                </div>
                <div className="text-xs text-white/50 mt-1 min-h-[2.5em]">
                  {t.tagline}
                </div>
                {highlight && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-nebula-300">
                    Your plan
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Body rows. */}
        {rows.map((row, i) => (
          <div
            key={row.label}
            className={`grid grid-cols-[minmax(0,1fr)_repeat(3,minmax(0,1fr))] ${
              i % 2 === 0 ? 'bg-white/[0.01]' : ''
            }`}
          >
            <div className="p-3 text-sm text-white/70 border-t border-white/[0.04]">
              {row.label}
            </div>
            {PUBLIC_TIERS.map((t) => {
              const highlight = t.id === currentId;
              return (
                <div
                  key={t.id}
                  className={`p-3 text-sm text-white/80 text-center border-t border-l border-white/[0.04] ${
                    highlight ? 'bg-nebula-500/[0.06]' : ''
                  }`}
                >
                  {row.render(t)}
                </div>
              );
            })}
          </div>
        ))}

        {/* Footer CTA row — spans all three tier columns. */}
        <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,minmax(0,1fr))] border-t border-white/[0.08]">
          <div className="p-4">&nbsp;</div>
          <div className="p-4 col-span-3 border-l border-white/[0.06] flex justify-center bg-white/[0.02]">
            {isAuthenticated ? (
              <Link
                to="/profile"
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/15 transition-colors"
              >
                Manage your plan
              </Link>
            ) : (
              <Link
                to="/"
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-nebula-500 hover:bg-nebula-400 text-white shadow-nebula transition-colors"
              >
                Sign in to get started
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
